// lightLeak — rare warm orange-to-pink streak across one corner. Mimics the
// Huji "light leaked through the cardboard" defect that disposables sometimes
// show. Per the spec, fires on ~30% of photos — gated on the seeded rng so the
// same photoId always produces the same leak (or absence of one).
//
// When firing: pick a corner (top-left or bottom-right, also seeded) and
// composite a linear gradient (orange → pink → transparent) in screen mode.

import { type FilterContext, eachPixel, getImageData, putImageData, screenBlend } from './_helpers'

// Bumped 0.3 → 0.4 → 0.7 across iterations — F-Wave β user feedback was that the
// Polaroid "warm" feel was perceived as "filter only fires on flash" because the
// halation-style highlights were missing. With the new always-on bloom step in
// place, leaks are still probabilistic but land on the majority of photos so the
// brand identity reads at first glance.
export const FIRE_PROBABILITY = 0.7

// Gradient stops — orange to pink to transparent. Alpha peaks at 0.4 mid-way.
const STOP_ORANGE: [number, number, number, number] = [255, 122, 58, 0]
const STOP_PINK: [number, number, number, number] = [255, 90, 169, 0.4]
const STOP_TRANSPARENT: [number, number, number, number] = [255, 90, 169, 0]

export function lightLeak(c: FilterContext): void {
  // Consume two rng draws so callers can rely on a fixed sequence regardless
  // of whether the leak fires.
  const fireRoll = c.rng()
  const cornerRoll = c.rng()
  if (fireRoll >= FIRE_PROBABILITY) return

  const fromTopLeft = cornerRoll < 0.5

  // Build a unit-length gradient axis from one corner toward the opposite.
  // We'll project each pixel onto this axis to get t ∈ [0, 1].
  const ax = fromTopLeft ? 0 : c.width - 1
  const ay = fromTopLeft ? 0 : c.height - 1
  const bx = fromTopLeft ? c.width - 1 : 0
  const by = fromTopLeft ? c.height - 1 : 0
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy || 1

  const img = getImageData(c.ctx)
  const width = c.width

  eachPixel(img, (r, g, b, a, i) => {
    const pixelIdx = i / 4
    const px = pixelIdx % width
    const py = Math.floor(pixelIdx / width)
    let t = ((px - ax) * dx + (py - ay) * dy) / lenSq
    if (t < 0) t = 0
    else if (t > 1) t = 1

    const [or, og, ob, oa] = sampleLeak(t)
    if (oa <= 0) return [r, g, b, a]

    const sr = screenBlend(r, or)
    const sg = screenBlend(g, og)
    const sb = screenBlend(b, ob)
    return [r + (sr - r) * oa, g + (sg - g) * oa, b + (sb - b) * oa, a]
  })
  putImageData(c.ctx, img)
}

function sampleLeak(t: number): [number, number, number, number] {
  // Three-stop interpolation: [0]=orange transparent, [0.4]=pink @ 0.4 alpha,
  // [1]=pink transparent. We mostly affect the front 40% of the diagonal.
  if (t <= 0.4) {
    const f = t / 0.4
    return lerpStop(STOP_ORANGE, STOP_PINK, f)
  }
  const f = (t - 0.4) / 0.6
  return lerpStop(STOP_PINK, STOP_TRANSPARENT, f)
}

function lerpStop(
  a: [number, number, number, number],
  b: [number, number, number, number],
  f: number,
): [number, number, number, number] {
  return [
    a[0] + (b[0] - a[0]) * f,
    a[1] + (b[1] - a[1]) * f,
    a[2] + (b[2] - a[2]) * f,
    a[3] + (b[3] - a[3]) * f,
  ]
}
